import { useAcl, useAppConfig } from '@/features/acl/acl-provider';
import { Can } from '@/features/acl/can';
import { useCustomerBasis } from '@/features/customer-detail/hooks/use-customer-basis';
import { DialogForm } from '@/features/dialog/dialog-form';
import { EntitySearchCombobox } from '@/features/shared/components/form-items-with-fetch/entity-search-combobox';
import { useLocale } from '@/lib/hooks/use-locale';
import { logger } from '@/lib/logger';
import type { DraftFindDefaultHeaderResponse, SalesDraftType } from '@/server/types/drafts-types';
import { SearchEntity } from '@/server/types/search-types';
import { useEffect } from 'react';
import type { Control, SubmitHandler } from 'react-hook-form';
import { Controller, useController, useForm, useWatch } from 'react-hook-form';
import { useSalesDraftDefaults } from '../hooks/use-sales-draft-defaults';
import { SearchStorageLocationCombobox } from './search-storage-location-combobox';

interface FormInputs {
    customer_id: string;
    storage_id: string;
    billing_address: DraftFindDefaultHeaderResponse['invoice_address'];
    shipping_address: DraftFindDefaultHeaderResponse['shipping_address'];
    shipping_type_id: string;
    delivery_date: string;
    due_date: string;
    external: Record<string, unknown>;
    type: SalesDraftType;
    notes: string;
}

export interface DraftOrderFormProps {
    customerId?: string;
    storageId?: string;
    onSubmit: SubmitHandler<FormInputs>;
    onCancel: () => void;
    isLoading?: boolean;
}

export const DraftOrderForm = ({
    customerId,
    storageId,
    onSubmit,
    onCancel,
    isLoading,
}: DraftOrderFormProps) => {
    const { t } = useLocale();
    const { transactionDraftTypes: availableTypes } = useAppConfig();

    const { control, formState, handleSubmit, watch, setValue } = useForm<FormInputs>({
        defaultValues: {
            customer_id: customerId,
            storage_id: storageId,
        },
        mode: 'all',
    });

    const watchCustomerId = watch('customer_id');

    const customerBasis = useCustomerBasis({
        customerId,
    });

    const orderType = availableTypes?.[0] ?? 'ORDER';

    const { defaults, isLoadingDefault } = useSalesDraftDefaults({
        customerId: watchCustomerId,
        orderType,
    });

    useEffect(() => {
        logger.debug('defaults', defaults);
        // This values is required to be able to create the draft
        setValue('type', orderType);

        if (defaults) {
            setValue('billing_address', defaults.invoice_address);
            // if shipping address is empty, use billing address
            setValue(
                'shipping_address',
                Object.values(defaults.shipping_address).some(Boolean)
                    ? defaults.shipping_address
                    : defaults.invoice_address,
            );
            setValue('shipping_type_id', defaults.shipping_type);
            setValue('delivery_date', defaults.desired_delivery_date);
            setValue('due_date', defaults.due_date);
            setValue('external', {
                document_type: defaults.sales_order_type,
            });

            setValue('storage_id', defaults.storage_id, {
                shouldDirty: true,
                shouldValidate: true,
            });
        }
    }, [defaults, setValue]);

    return (
        <>
            <DialogForm.Content>
                <DialogForm.ItemList col>
                    <DialogForm.Item>
                        <DialogForm.Label>{t('sales_draft__new_form___customer')}</DialogForm.Label>
                        <DialogForm.Body>
                            <Controller
                                name="customer_id"
                                control={control}
                                rules={{
                                    required: true,
                                }}
                                render={({ field: { value, onChange } }) => {
                                    return (
                                        <EntitySearchCombobox
                                            className="w-full"
                                            entities={[SearchEntity.Customer]}
                                            defaultValue={
                                                value && customerBasis.data?.customerName
                                                    ? [
                                                          {
                                                              id: value,
                                                              name: customerBasis.data.customerName,
                                                              entity: SearchEntity.Customer,
                                                          },
                                                      ]
                                                    : undefined
                                            }
                                            inputPlaceholder={t('placeholder__select_customer')}
                                            onValueChange={(value) => {
                                                const [item] = value;
                                                onChange(item?.id || '');
                                            }}
                                        />
                                    );
                                }}
                            />
                        </DialogForm.Body>
                    </DialogForm.Item>
                    {/**
                     * OK here it is tricky, we need to check if the user has the permission to read the storage_id field
                     * because eBootis does not use this field to submit the sales draft
                     */}
                    <Can I="read" a="SalesDraft" field="storage_id">
                        <DialogForm.Item>
                            <DialogForm.Label>
                                {t('sales_draft__new_form___storage_location')}
                            </DialogForm.Label>
                            <DialogForm.Body>
                                <SearchLocationByCustomer
                                    control={control}
                                    isLoadingDefault={!!isLoadingDefault}
                                />
                            </DialogForm.Body>
                        </DialogForm.Item>
                    </Can>
                </DialogForm.ItemList>
            </DialogForm.Content>
            <DialogForm.SubmitBlock
                disabled={!formState.isValid}
                isLoading={formState.isSubmitting || isLoadingDefault || isLoading}
                onCancel={onCancel}
                onSubmit={handleSubmit(onSubmit)}
            />
        </>
    );
};

const SearchLocationByCustomer = ({
    control,
    isLoadingDefault,
}: {
    control: Control<FormInputs>;
    isLoadingDefault: boolean;
}) => {
    const acl = useAcl();

    const customerId = useWatch({
        control,
        name: 'customer_id',
        // defaultValue: 'default', // default value before the render
    });

    const { field } = useController({
        name: 'storage_id',
        control,
        rules: {
            required: true,
        },
    });

    // TODO: move this to sales-draft feature folder
    return (
        <SearchStorageLocationCombobox
            customerId={customerId}
            value={field.value}
            onChange={field.onChange}
            disabled={!acl.can('update', 'SalesDraft', 'storage_id')}
            isLoading={isLoadingDefault}
        />
    );
};
